import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, UserCheck, UserX, TrendingUp, MessageCircle, Heart, 
  Search, ChevronLeft, ChevronRight, Eye, Ban, Trash2, Key, 
  Shield, ShieldOff, X, AlertTriangle, RefreshCw, Download, Sun, Moon,
  BarChart3, Calendar, Mail, Edit3, Save, Lock, LogIn
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  
  // Password verification state
  const [isVerified, setIsVerified] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  // State for stat detail modal
  const [showStatModal, setShowStatModal] = useState(false);
  const [statDetail, setStatDetail] = useState(null);
  const [statLoading, setStatLoading] = useState(false);
  
  // State for edit profile modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Always require password each time the Admin Console is opened (no persisted bypass)
  useEffect(() => {
    sessionStorage.removeItem('adminVerified');
    setIsVerified(false);
  }, []);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    if (isVerified) {
      fetchStats();
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [user, navigate, isVerified]);

  useEffect(() => {
    if (isVerified) {
      fetchUsers();
    }
  }, [search, statusFilter, page, isVerified]);

  // Password verification handler
  const handlePasswordVerify = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    setVerifyError('');
    
    try {
      // Verify the logged-in admin's password without triggering the 2FA/OTP flow
      const response = await axios.post('/auth/verify-password', {
        password: verifyPassword
      });
      
      if (response.data.verified) {
        // Password verified successfully
        setIsVerified(true);
        toast.success('Access granted to Admin Dashboard');
        setLoading(true);
        fetchStats();
        fetchUsers();
      }
    } catch (error) {
      setVerifyError('Incorrect password. Please try again.');
      setVerifyPassword('');
    } finally {
      setVerifyLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get('/admin/dashboard/users', {
        params: { search, status: statusFilter, page, limit: 10 }
      });
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
      setLoading(false);
    }
  };

  const fetchStatDetail = async (statType) => {
    setStatLoading(true);
    setShowStatModal(true);
    try {
      const response = await axios.get(`/admin/dashboard/stats/${statType}`);
      setStatDetail(response.data);
    } catch (error) {
      console.error('Error fetching stat detail:', error);
      toast.error('Failed to load details');
      setShowStatModal(false);
    } finally {
      setStatLoading(false);
    }
  };

  const viewUserDetail = async (userId) => {
    try {
      const response = await axios.get(`/admin/dashboard/users/${userId}`);
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  const openEditModal = async (userObj) => {
    try {
      const response = await axios.get(`/admin/dashboard/users/${userObj.id}`);
      const userData = response.data;
      setEditingUser(userData);
      setEditForm({
        full_name: userData.full_name || '',
        username: userData.username || '',
        email: userData.email || '',
        age: userData.age || 18,
        location: userData.location || '',
        bio: userData.bio || '',
        looking_for: userData.looking_for || '',
        relationship_status: userData.relationship_status || '',
        alcohol_preference: userData.alcohol_preference || '',
        smoking_preference: userData.smoking_preference || '',
        religious_beliefs: userData.religious_beliefs || '',
        body_type: userData.body_type || '',
        date_someone_with_children: userData.date_someone_with_children || '',
        is_admin: userData.is_admin || false
      });
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to load user data');
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const saveUserProfile = async () => {
    setEditSaving(true);
    try {
      const response = await axios.post('/admin/dashboard/update-profile', {
        user_id: editingUser.id,
        ...editForm
      });
      
      toast.success(response.data.message);
      setShowEditModal(false);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setEditSaving(false);
    }
  };

  const openActionModal = (type, userObj) => {
    setSelectedUser(userObj);
    setActionType(type);
    setActionReason('');
    setNewPassword('');
    setShowActionModal(true);
  };

  const performAction = async () => {
    try {
      const response = await axios.post('/admin/dashboard/user-action', {
        user_id: selectedUser.id,
        action: actionType,
        reason: actionReason,
        new_password: newPassword
      });
      
      toast.success(response.data.message);
      if (response.data.new_password) {
        toast.info(`New password: ${response.data.new_password}`, { duration: 10000 });
      }
      
      setShowActionModal(false);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const exportUsers = () => {
    const csv = [
      ['Email', 'Username', 'Full Name', 'Location', 'Status', 'Created At'].join(','),
      ...users.map(u => [
        u.email,
        u.username,
        u.full_name,
        u.location,
        u.is_suspended ? 'Suspended' : 'Active',
        new Date(u.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'eternitysingles_users.csv';
    a.click();
    toast.success('Users exported to CSV');
  };

  const getColorClass = (color) => {
    const colors = {
      emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      rose: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
      red: 'bg-red-500/20 text-red-400 border-red-500/30',
      yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      indigo: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      fuchsia: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30',
      gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    return colors[color] || colors.gray;
  };

  // Stat Card Component
  const StatCard = ({ label, value, icon: Icon, color, statType }) => (
    <div 
      onClick={() => fetchStatDetail(statType)}
      className={`${isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-gray-200 hover:bg-gray-50'} rounded-xl p-4 border cursor-pointer transition-all transform hover:scale-[1.02] hover:shadow-lg`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{label}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
        </div>
        <Icon className={`w-10 h-10 ${color}`} />
      </div>
      <div className={`mt-2 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'} flex items-center`}>
        <BarChart3 className="w-3 h-3 mr-1" />
        Click for details
      </div>
    </div>
  );

  // Input styling classes
  const inputClass = `w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400'} focus:ring-2 focus:ring-emerald-500 focus:outline-none`;
  const labelClass = `block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`;

  // Password verification screen
  if (!isVerified) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-gray-100 via-white to-gray-100'} flex items-center justify-center p-4`}>
        <div className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-2xl border shadow-2xl max-w-md w-full overflow-hidden`}>
          {/* Header */}
          <div className={`p-6 ${isDark ? 'bg-slate-700/50' : 'bg-emerald-50'} border-b ${isDark ? 'border-slate-600' : 'border-emerald-100'}`}>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className={`text-2xl font-bold text-center mt-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Admin Verification
            </h1>
            <p className={`text-center mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Please enter your password to access the Admin Dashboard
            </p>
          </div>
          
          {/* Form */}
          <form onSubmit={handlePasswordVerify} className="p-6 space-y-6">
            {/* User Info */}
            <div className={`flex items-center space-x-3 p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                {user?.full_name?.charAt(0) || '?'}
              </div>
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{user?.full_name}</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user?.email}</p>
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className={labelClass}>
                <Lock className="w-4 h-4 inline mr-2" />
                Password
              </label>
              <input
                type="password"
                value={verifyPassword}
                onChange={(e) => setVerifyPassword(e.target.value)}
                placeholder="Enter your password"
                className={inputClass}
                required
                autoFocus
              />
            </div>

            {/* Error Message */}
            {verifyError && (
              <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span>{verifyError}</span>
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-3">
              <button
                type="submit"
                disabled={verifyLoading || !verifyPassword}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {verifyLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Verify & Access Dashboard</span>
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className={`w-full py-3 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} font-medium rounded-lg transition-colors`}
              >
                Back to App
              </button>
            </div>
          </form>
          
          {/* Footer */}
          <div className={`px-6 py-4 ${isDark ? 'bg-slate-700/30 border-slate-700' : 'bg-gray-50 border-gray-100'} border-t`}>
            <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              🔒 This verification is required for security purposes
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-gray-100 via-white to-gray-100'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={isDark ? 'text-white' : 'text-gray-800'}>Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-gray-100 via-white to-gray-100'} p-4 md:p-8`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'} flex items-center space-x-3`}>
              <Shield className="w-8 h-8 text-emerald-500" />
              <span>Admin Dashboard</span>
            </h1>
            <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} mt-1`}>Manage users and monitor platform activity</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-yellow-400' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'}`}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className={`px-4 py-2 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'} rounded-lg transition-colors`}
            >
              Back to App
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Users" value={stats.total_users} icon={Users} color={isDark ? 'text-white' : 'text-gray-800'} statType="total_users" />
            <StatCard label="Active Users" value={stats.active_users} icon={UserCheck} color="text-emerald-400" statType="active_users" />
            <StatCard label="New This Week" value={stats.new_users_week} icon={TrendingUp} color="text-blue-400" statType="new_users_week" />
            <StatCard label="Suspended" value={stats.suspended_users} icon={UserX} color="text-red-400" statType="suspended" />
            <StatCard label="Total Matches" value={stats.total_matches} icon={Heart} color="text-pink-400" statType="total_matches" />
            <StatCard label="Total Messages" value={stats.total_messages} icon={MessageCircle} color="text-purple-400" statType="total_messages" />
            <StatCard label="Men" value={stats.gender_distribution?.men || 0} icon={Users} color="text-cyan-400" statType="men" />
            <StatCard label="Women" value={stats.gender_distribution?.women || 0} icon={Users} color="text-rose-400" statType="women" />
          </div>
        )}

        {/* Users Section */}
        <div className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-xl border overflow-hidden`}>
          <div className={`p-4 ${isDark ? 'border-slate-700' : 'border-gray-200'} border-b`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>User Management</h2>
              
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className={`pl-10 pr-4 py-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400'} border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-full md:w-64`}
                  />
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className={`px-4 py-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'} border rounded-lg focus:ring-2 focus:ring-emerald-500`}
                >
                  <option value="all">All Users</option>
                  <option value="active">Active Only</option>
                  <option value="suspended">Suspended Only</option>
                </select>

                <button onClick={exportUsers} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2">
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>

                <button
                  onClick={() => { fetchStats(); fetchUsers(); }}
                  className={`p-2 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-colors`}
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={isDark ? 'bg-slate-700/50' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-500'} uppercase tracking-wider`}>User</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-500'} uppercase tracking-wider`}>Email</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-500'} uppercase tracking-wider`}>Location</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-500'} uppercase tracking-wider`}>Status</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-500'} uppercase tracking-wider`}>Joined</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-500'} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                {usersLoading ? (
                  <tr>
                    <td colSpan="6" className={`px-4 py-8 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className={`px-4 py-8 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className={`${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'} transition-colors`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                            {u.full_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className={`${isDark ? 'text-white' : 'text-gray-800'} font-medium`}>{u.full_name}</p>
                            <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>@{u.username}</p>
                          </div>
                          {u.is_admin && (
                            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex items-center space-x-1">
                              <Shield className="w-3 h-3" />
                              <span>Admin</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{u.email}</td>
                      <td className={`px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{u.location || 'N/A'}</td>
                      <td className="px-4 py-3">
                        {u.is_suspended ? (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">Suspended</span>
                        ) : (
                          <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Active</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-600'} text-sm`}>
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => viewUserDetail(u.id)}
                            className={`p-2 ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-600' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(u)}
                            className={`p-2 text-blue-400 hover:text-blue-300 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                            title="Edit Profile"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {u.is_suspended ? (
                            <button
                              onClick={() => openActionModal('unsuspend', u)}
                              className={`p-2 text-emerald-400 hover:text-emerald-300 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                              title="Unsuspend"
                            >
                              <ShieldOff className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => openActionModal('suspend', u)}
                              className={`p-2 text-amber-400 hover:text-amber-300 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                              title="Suspend"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openActionModal('reset_password', u)}
                            className={`p-2 text-blue-400 hover:text-blue-300 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openActionModal('delete', u)}
                            className={`p-2 text-red-400 hover:text-red-300 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`p-4 ${isDark ? 'border-slate-700' : 'border-gray-200'} border-t flex items-center justify-between`}>
              <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Page {page} of {totalPages}</p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`p-2 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`p-2 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit Profile Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border`}>
              <div className={`p-6 ${isDark ? 'border-slate-700' : 'border-gray-200'} border-b flex items-center justify-between sticky top-0 ${isDark ? 'bg-slate-800' : 'bg-white'} z-10`}>
                <div className="flex items-center space-x-3">
                  <Edit3 className="w-6 h-6 text-blue-500" />
                  <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Edit Profile: {editingUser.full_name}
                  </h3>
                </div>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className={`p-2 ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Full Name</label>
                      <input type="text" value={editForm.full_name} onChange={(e) => handleEditFormChange('full_name', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Username</label>
                      <input type="text" value={editForm.username} onChange={(e) => handleEditFormChange('username', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Email</label>
                      <input type="email" value={editForm.email} onChange={(e) => handleEditFormChange('email', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Age</label>
                      <input type="number" value={editForm.age} onChange={(e) => handleEditFormChange('age', parseInt(e.target.value) || 18)} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Location</label>
                      <input type="text" value={editForm.location} onChange={(e) => handleEditFormChange('location', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Looking For</label>
                      <select value={editForm.looking_for} onChange={(e) => handleEditFormChange('looking_for', e.target.value)} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="Man">Man</option>
                        <option value="Woman">Woman</option>
                        <option value="Anyone">Anyone</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className={labelClass}>Bio</label>
                  <textarea 
                    value={editForm.bio} 
                    onChange={(e) => handleEditFormChange('bio', e.target.value)} 
                    placeholder="User's bio..."
                    rows={3}
                    className={inputClass}
                  />
                </div>

                {/* Preferences */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Preferences</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Relationship Status</label>
                      <select value={editForm.relationship_status} onChange={(e) => handleEditFormChange('relationship_status', e.target.value)} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="Single">Single</option>
                        <option value="Divorced">Divorced</option>
                        <option value="Widowed">Widowed</option>
                        <option value="Separated">Separated</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Body Type</label>
                      <select value={editForm.body_type} onChange={(e) => handleEditFormChange('body_type', e.target.value)} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="Slim">Slim</option>
                        <option value="Athletic">Athletic</option>
                        <option value="Average">Average</option>
                        <option value="Curvy">Curvy</option>
                        <option value="Plus Size">Plus Size</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Alcohol</label>
                      <select value={editForm.alcohol_preference} onChange={(e) => handleEditFormChange('alcohol_preference', e.target.value)} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="Never">Never</option>
                        <option value="Socially">Socially</option>
                        <option value="Often">Often</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Smoking</label>
                      <select value={editForm.smoking_preference} onChange={(e) => handleEditFormChange('smoking_preference', e.target.value)} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="Never">Never</option>
                        <option value="Socially">Socially</option>
                        <option value="Often">Often</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Religious Beliefs</label>
                      <select value={editForm.religious_beliefs} onChange={(e) => handleEditFormChange('religious_beliefs', e.target.value)} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="Christian">Christian</option>
                        <option value="Muslim">Muslim</option>
                        <option value="Jewish">Jewish</option>
                        <option value="Hindu">Hindu</option>
                        <option value="Buddhist">Buddhist</option>
                        <option value="Spiritual">Spiritual</option>
                        <option value="Agnostic">Agnostic</option>
                        <option value="Atheist">Atheist</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Date Someone with Children</label>
                      <select value={editForm.date_someone_with_children} onChange={(e) => handleEditFormChange('date_someone_with_children', e.target.value)} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Open to it">Open to it</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Admin Settings */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Admin Settings</h4>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.is_admin}
                      onChange={(e) => handleEditFormChange('is_admin', e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className={isDark ? 'text-white' : 'text-gray-800'}>Grant Admin Privileges</span>
                  </label>
                  <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Note: Only super admins can modify admin status
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className={`p-6 ${isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'} border-t sticky bottom-0`}>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className={`flex-1 px-4 py-2 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-colors`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveUserProfile}
                    disabled={editSaving}
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {editSaving ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stat Detail Modal */}
        {showStatModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-2xl max-w-lg w-full border shadow-2xl`}>
              <div className={`p-6 ${isDark ? 'border-slate-700' : 'border-gray-200'} border-b flex items-center justify-between`}>
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-6 h-6 text-emerald-500" />
                  <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {statLoading ? 'Loading...' : statDetail?.title || 'Statistics Detail'}
                  </h3>
                </div>
                <button 
                  onClick={() => { setShowStatModal(false); setStatDetail(null); }}
                  className={`p-2 ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                </button>
              </div>
              
              <div className="p-6">
                {statLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-emerald-500" />
                    <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Loading details...</p>
                  </div>
                ) : statDetail ? (
                  <div className="space-y-6">
                    <div className={`text-center p-4 ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} rounded-xl`}>
                      <p className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{statDetail.total}</p>
                      <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Total</p>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Breakdown</h4>
                      {statDetail.breakdown?.map((item, index) => (
                        <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${getColorClass(item.color)}`}>
                          <span className="font-medium">{item.label}</span>
                          <span className="text-2xl font-bold">{item.value}</span>
                        </div>
                      ))}
                    </div>
                    
                    {statDetail.recent && statDetail.recent.length > 0 && (
                      <div className="space-y-3">
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Recent Signups</h4>
                        <div className="space-y-2">
                          {statDetail.recent.map((user, index) => (
                            <div key={index} className={`flex items-center justify-between p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} rounded-lg`}>
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                  {user.full_name?.charAt(0) || '?'}
                                </div>
                                <div>
                                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{user.full_name}</p>
                                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.location || 'No location'}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1 text-xs text-slate-400">
                                <Calendar className="w-3 h-3" />
                                <span>{new Date(user.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {statDetail.users && statDetail.users.length > 0 && (
                      <div className="space-y-3">
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Suspended Users</h4>
                        <div className="space-y-2">
                          {statDetail.users.map((user, index) => (
                            <div key={index} className={`p-3 ${isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-100'} border rounded-lg`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <Mail className="w-4 h-4 text-red-400" />
                                  <span className={isDark ? 'text-white' : 'text-gray-800'}>{user.email}</span>
                                </div>
                              </div>
                              {user.suspended_reason && (
                                <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                  Reason: {user.suspended_reason}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={`text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>No data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* User Detail Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border`}>
              <div className={`p-6 ${isDark ? 'border-slate-700' : 'border-gray-200'} border-b flex items-center justify-between`}>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>User Details</h3>
                <button onClick={() => setShowUserModal(false)} className={`p-2 ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-lg`}>
                  <X className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {selectedUser.full_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <h4 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{selectedUser.full_name}</h4>
                    <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>@{selectedUser.username}</p>
                    {selectedUser.is_admin && (
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Admin</span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Email</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.email}</p>
                  </div>
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Location</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.location || 'N/A'}</p>
                  </div>
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Age</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.age}</p>
                  </div>
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Looking For</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.looking_for}</p>
                  </div>
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Matches</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.match_count || 0}</p>
                  </div>
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Messages</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.message_count || 0}</p>
                  </div>
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Status</p>
                    <p className={selectedUser.is_suspended ? 'text-red-400' : 'text-emerald-400'}>
                      {selectedUser.is_suspended ? 'Suspended' : 'Active'}
                    </p>
                  </div>
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm`}>Joined</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                {selectedUser.bio && (
                  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-sm mb-1`}>Bio</p>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.bio}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Confirmation Modal */}
        {showActionModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-2xl max-w-md w-full border`}>
              <div className={`p-6 ${isDark ? 'border-slate-700' : 'border-gray-200'} border-b`}>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'} flex items-center space-x-2`}>
                  <AlertTriangle className={`w-6 h-6 ${actionType === 'delete' ? 'text-red-500' : 'text-amber-500'}`} />
                  <span>
                    {actionType === 'suspend' && 'Suspend User'}
                    {actionType === 'unsuspend' && 'Unsuspend User'}
                    {actionType === 'delete' && 'Delete User'}
                    {actionType === 'reset_password' && 'Reset Password'}
                  </span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>
                  {actionType === 'suspend' && `Are you sure you want to suspend ${selectedUser.email}?`}
                  {actionType === 'unsuspend' && `Are you sure you want to unsuspend ${selectedUser.email}?`}
                  {actionType === 'delete' && `Are you sure you want to permanently delete ${selectedUser.email}? This action cannot be undone.`}
                  {actionType === 'reset_password' && `Reset password for ${selectedUser.email}?`}
                </p>

                {actionType === 'suspend' && (
                  <div>
                    <label className={`block text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'} mb-1`}>Reason (optional)</label>
                    <textarea
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      className={`w-full px-3 py-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'} border rounded-lg`}
                      rows={3}
                      placeholder="Enter reason for suspension..."
                    />
                  </div>
                )}

                {actionType === 'reset_password' && (
                  <div>
                    <label className={`block text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'} mb-1`}>New Password (leave empty for auto-generated)</label>
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`w-full px-3 py-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'} border rounded-lg`}
                      placeholder="Enter new password..."
                    />
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowActionModal(false)}
                    className={`flex-1 px-4 py-2 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-colors`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={performAction}
                    className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${
                      actionType === 'delete' ? 'bg-red-600 hover:bg-red-700' :
                      actionType === 'unsuspend' ? 'bg-emerald-600 hover:bg-emerald-700' :
                      'bg-amber-600 hover:bg-amber-700'
                    }`}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
